/**
 * The company tracker (website roadmap S18 — TornStats' company pages, Torn Intel's company tracker and advisor): one
 * day's snapshot of a company as the server keeps it for that company's Omni-Torn members (terms 9), and the reports
 * the website's Company page draws from those days — profit and expenses, the week's recap, the income heatmap, the
 * weekly star forecast, pricing advice and each employee's effectiveness over time. Pure, browser-safe; one copy for the
 * server and the website.
 *
 * Who sees what (apps/api/src/company-share.ts): every member of the company who agreed to COMPANY_SHARE_TERMS sees the
 * company's figures; the staff list (names, positions, wages, effectiveness) only goes back to its director.
 */

import { cleanCompanyDay, tornDay, type Company, type CompanyDay, type Competitor } from './company';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);
const str = (x: unknown, max = 60): string | null => (typeof x === 'string' && x.trim() ? x.trim().slice(0, max) : null);
const DAY = 86_400_000;

/**
 * From this terms version on, a tracked player's daily read of their company is kept on the server as the company's
 * snapshot and shared with that company's Omni-Torn employees and director (who also must have agreed to it to see it).
 * Below it, nothing is kept for the company or shown from it (the player's own day by day stays COMPANY_HISTORY_TERMS).
 */
export const COMPANY_SHARE_TERMS = 9;
/** Days a company snapshot is kept. */
export const COMPANY_SHARE_KEEP_DAYS = 400;
/** A membership read by the server longer ago than this no longer opens the company's days. */
export const COMPANY_MEMBER_FRESH_MS = 3 * DAY;

/**
 * One employee on one day (the director's view; served only to the director): exactly what the terms name — name,
 * position, wage and Torn's total effectiveness. Never their working stats or effectiveness parts (addiction…): the
 * director's page shows those live from Torn's own answer, never from what's kept.
 */
export interface StaffDay {
  id: number;
  name: string;
  position: string;
  wage: number | null;
  effectiveness: number | null;
}

/** One product on one day (the director's view: what it sold in Torn's last day, at what price). */
export interface StockDay {
  name: string;
  price: number;
  cost: number;
  rrp: number;
  inStock: number;
  onOrder: number;
  soldAmount: number;
  soldWorth: number;
}

/** A company's day as the server keeps it for the company (CompanyDay plus the director's view of staff and stock). */
export interface CompanySnapshot extends CompanyDay {
  /** Every employee's wage together, a day (director's view). */
  wages: number | null;
  stock: StockDay[];
  /** Director's view; empty in what employees get. */
  staff: StaffDay[];
}

export function companySnapshotOf(c: Company, atMs: number, director: boolean): CompanySnapshot {
  const day: CompanyDay = {
    day: tornDay(atMs),
    at: atMs,
    companyId: c.id,
    name: c.name,
    type: c.type,
    rating: c.rating,
    dailyIncome: c.dailyIncome,
    dailyCustomers: c.dailyCustomers,
    weeklyIncome: c.weeklyIncome,
    weeklyCustomers: c.weeklyCustomers ?? null,
    employees: c.employees.length || c.employeesHired,
    capacity: c.employeesCapacity,
    popularity: director ? c.popularity : null,
    efficiency: director ? c.efficiency : null,
    environment: director ? c.environment : null,
    funds: director ? c.funds : null,
    advertising: director ? c.advertising : null,
    // The company's value isn't among the figures the terms name for the company: never kept for it.
    value: null,
    director,
  };
  const withWage = director ? c.employees.filter((e) => e.wage !== null) : [];
  return {
    ...day,
    wages: withWage.length ? withWage.reduce((s, e) => s + (e.wage ?? 0), 0) : null,
    stock: director ? c.stock.map((p) => ({ name: p.name, price: p.price, cost: p.cost, rrp: p.rrp, inStock: p.inStock, onOrder: p.onOrder, soldAmount: p.soldAmount, soldWorth: p.soldWorth })) : [],
    staff: director
      ? c.employees.map((e) => ({ id: e.id, name: e.name, position: e.position, wage: e.wage, effectiveness: e.effectiveness }))
      : [],
  };
}

const cap = (n: number | null, lim = 1e15): number | null => (n !== null && Math.abs(n) <= lim ? n : null);

/** A snapshot from outside (the database): only known fields with sane values; null when it isn't one. */
export function cleanCompanySnapshot(x: unknown, now: number): CompanySnapshot | null {
  const base = cleanCompanyDay(x, now);
  const o = obj(x);
  if (!base || !o) return null;
  const stock = (Array.isArray(o.stock) ? o.stock : []).slice(0, 50).flatMap((v): StockDay[] => {
    const p = obj(v);
    const name = str(p?.name);
    if (!p || !name) return [];
    const n = (k: string) => cap(num(p[k])) ?? 0;
    return [{ name, price: n('price'), cost: n('cost'), rrp: n('rrp'), inStock: n('inStock'), onOrder: n('onOrder'), soldAmount: n('soldAmount'), soldWorth: n('soldWorth') }];
  });
  const staff = (Array.isArray(o.staff) ? o.staff : []).slice(0, 100).flatMap((v): StaffDay[] => {
    const e = obj(v);
    const id = num(e?.id);
    if (!e || !id || !Number.isInteger(id) || id <= 0) return [];
    return [{ id, name: str(e.name, 40) ?? `#${id}`, position: str(e.position, 40) ?? 'Unassigned', wage: cap(num(e.wage)), effectiveness: cap(num(e.effectiveness), 1e4) }];
  });
  return { ...base, value: null, wages: cap(num(o.wages)), stock: base.director ? stock : [], staff: base.director ? staff : [] };
}

/** What a member who isn't the director gets: the company's figures, no staff list (and never the company's value). */
export const forEmployees = (s: CompanySnapshot): CompanySnapshot => ({ ...s, value: null, staff: [] });

/** GET /api/company/shared: the company's kept days for one of its members. */
export interface CompanyShareAnswer {
  /** The terms version sharing needs (COMPANY_SHARE_TERMS) and whether the asker agreed to it. */
  terms: number;
  agreed: boolean;
  /** The asker's company as the server last read it with their own key (null: not read, or not in one). */
  member: { companyId: number; director: boolean; at: number } | null;
  /** Why there are no days, in plain words (null when there are). */
  note: string | null;
  /** Oldest first. */
  days: CompanySnapshot[];
  /** How many of its members' keys keep it (never who). */
  members: number;
}

// ---------------------------------------------------------------- profit and expenses

export interface DayMoney {
  income: number | null;
  wages: number | null;
  advertising: number | null;
  /** What the products sold cost the company (Σ cost × sold), when the stock is known. */
  goods: number | null;
  /** wages + advertising + goods (null when no part is known). */
  expenses: number | null;
  /** income − expenses (null without the director's view). */
  profit: number | null;
}

/**
 * A day's money: Torn's daily income less wages, the advertising budget and what the products sold cost. Every part is
 * Torn's figure for its last day except the wages and the advertising budget, which are as they stand when it was read.
 */
export function dayMoney(d: Pick<CompanySnapshot, 'dailyIncome' | 'advertising' | 'director'> & Partial<Pick<CompanySnapshot, 'wages' | 'stock'>>): DayMoney {
  const income = d.dailyIncome;
  const wages = d.wages ?? null;
  const advertising = d.director ? d.advertising : null;
  const goods = d.stock?.length ? d.stock.reduce((s, p) => s + p.cost * p.soldAmount, 0) : null;
  const parts = [wages, advertising, goods].filter((x): x is number => x !== null);
  const expenses = parts.length ? parts.reduce((a, b) => a + b, 0) : null;
  const profit = d.director && income !== null && expenses !== null ? income - expenses : null;
  return { income, wages, advertising, goods, expenses, profit };
}

/** A live company (director's view) as a day's money. */
export function companyMoney(c: Company, director: boolean): DayMoney {
  return dayMoney({ dailyIncome: c.dailyIncome, advertising: c.advertising, director, wages: director && c.employees.some((e) => e.wage !== null) ? c.employees.reduce((s, e) => s + (e.wage ?? 0), 0) : null, stock: director ? c.stock : [] });
}

/** Torn's "daily" figures read on a day are the day before's: the day the money was made. */
export const earnedDay = (day: string): string => tornDay(Date.parse(`${day}T00:00:00Z`) - DAY);

/** Days by Torn day (the newest read of a day wins; the director's view wins over an employee's). */
export function mergeDays<T extends CompanyDay>(...lists: ReadonlyArray<readonly T[]>): T[] {
  const by = new Map<string, T>();
  for (const list of lists)
    for (const d of list) {
      const old = by.get(d.day);
      if (!old || (d.director && !old.director) || (d.director === old.director && d.at > old.at)) by.set(d.day, d);
    }
  return [...by.values()].sort((a, b) => a.day.localeCompare(b.day));
}

// ---------------------------------------------------------------- the week's recap

export interface RecapSide {
  days: number;
  income: number | null;
  customers: number | null;
  profit: number | null;
  /** Averages over the days that have them. */
  popularity: number | null;
  efficiency: number | null;
  environment: number | null;
}

export interface CompanyRecap {
  /** The last `span` days kept and the `span` before them. */
  now: RecapSide;
  before: RecapSide;
  rating: { from: number | null; to: number | null };
  employees: { from: number | null; to: number | null };
  /** Director's view: who joined and who left between the first and last day of the recap (by staff lists). */
  joined: Array<{ id: number; name: string }>;
  left: Array<{ id: number; name: string }>;
  /** Plain-words lines. */
  lines: string[];
}

const sumOf = (xs: Array<number | null>): number | null => (xs.some((x) => x !== null) ? xs.reduce<number>((s, x) => s + (x ?? 0), 0) : null);
const avgOf = (xs: Array<number | null>): number | null => {
  const v = xs.filter((x): x is number => x !== null);
  return v.length ? Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10 : null;
};

function side(days: readonly CompanySnapshot[]): RecapSide {
  return {
    days: days.length,
    income: sumOf(days.map((d) => d.dailyIncome)),
    customers: sumOf(days.map((d) => d.dailyCustomers)),
    profit: sumOf(days.map((d) => dayMoney(d).profit)),
    popularity: avgOf(days.map((d) => d.popularity)),
    efficiency: avgOf(days.map((d) => d.efficiency)),
    environment: avgOf(days.map((d) => d.environment)),
  };
}

const pct = (a: number | null, b: number | null): number | null => (a !== null && b !== null && b !== 0 ? Math.round(((a - b) / Math.abs(b)) * 1000) / 10 : null);
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/** The last `span` days against the `span` before them (days oldest first, one company). */
export function companyRecap(days: readonly CompanySnapshot[], span = 7): CompanyRecap {
  const list = [...days].sort((a, b) => a.day.localeCompare(b.day));
  const last = list.at(-1);
  const cut = last ? tornDay(Date.parse(`${last.day}T00:00:00Z`) - (span - 1) * DAY) : '';
  const prevCut = last ? tornDay(Date.parse(`${last.day}T00:00:00Z`) - (2 * span - 1) * DAY) : '';
  const nowDays = list.filter((d) => d.day >= cut);
  const beforeDays = list.filter((d) => d.day >= prevCut && d.day < cut);
  const now = side(nowDays);
  const before = side(beforeDays);
  const first = nowDays[0];
  const rating = { from: beforeDays.at(-1)?.rating ?? first?.rating ?? null, to: last?.rating ?? null };
  const employees = { from: beforeDays.at(-1)?.employees ?? first?.employees ?? null, to: last?.employees ?? null };
  // Staff changes: the first and the last staff list in the span (director's view only).
  const staffed = [...beforeDays.slice(-1), ...nowDays].filter((d) => d.staff.length);
  const a = staffed[0];
  const b = staffed.at(-1);
  const joined = a && b && a !== b ? b.staff.filter((e) => !a.staff.some((x) => x.id === e.id)).map((e) => ({ id: e.id, name: e.name })) : [];
  const left = a && b && a !== b ? a.staff.filter((e) => !b.staff.some((x) => x.id === e.id)).map((e) => ({ id: e.id, name: e.name })) : [];
  const lines: string[] = [];
  if (!nowDays.length) return { now, before, rating, employees, joined, left, lines: ['No days kept yet.'] };
  const change = (what: string, a2: number | null, b2: number | null, f: (n: number) => string) => {
    if (a2 === null) return;
    const p = pct(a2, b2);
    lines.push(`${what}: ${f(a2)}${p !== null ? ` (${p > 0 ? '+' : ''}${p}% on the ${span} days before)` : ''}.`);
  };
  change('Income', now.income, before.income, usd);
  change('Customers', now.customers, before.customers, (n) => Math.round(n).toLocaleString('en-US'));
  change('Profit', now.profit, before.profit, usd);
  if (rating.from !== null && rating.to !== null && rating.from !== rating.to) lines.push(`Rating: ${rating.from} → ${rating.to} ★.`);
  if (employees.from !== null && employees.to !== null && employees.from !== employees.to) lines.push(`Staff: ${employees.from} → ${employees.to}.`);
  if (joined.length) lines.push(`Joined: ${joined.map((e) => e.name).join(', ')}.`);
  if (left.length) lines.push(`Left: ${left.map((e) => e.name).join(', ')}.`);
  if (now.popularity !== null && before.popularity !== null && Math.abs(now.popularity - before.popularity) >= 1) lines.push(`Popularity ${now.popularity > before.popularity ? 'up' : 'down'}: ${before.popularity}% → ${now.popularity}% on average.`);
  if (now.efficiency !== null && before.efficiency !== null && Math.abs(now.efficiency - before.efficiency) >= 1) lines.push(`Efficiency ${now.efficiency > before.efficiency ? 'up' : 'down'}: ${before.efficiency}% → ${now.efficiency}% on average.`);
  return { now, before, rating, employees, joined, left, lines };
}

// ---------------------------------------------------------------- the income heatmap

export interface Heatmap {
  /** Monday-first week rows (the week's Monday, YYYY-MM-DD), newest last. */
  weeks: Array<{ monday: string; cells: Array<{ day: string; v: number | null }> }>;
  /** Average per weekday (Monday first) over the days that have a figure. */
  weekdays: Array<number | null>;
  max: number;
  min: number;
}

/**
 * A figure per day the money was made (earnedDay), as weeks × weekdays (Monday first) — Torn Intel's heatmap. The
 * newest `weeks` weeks.
 */
export function companyHeatmap(days: readonly CompanySnapshot[] | readonly CompanyDay[], value: (d: CompanyDay) => number | null = (d) => d.dailyIncome, weeks = 12): Heatmap {
  const by = new Map<string, number>();
  for (const d of days) {
    const v = value(d);
    if (v !== null) by.set(earnedDay(d.day), v);
  }
  const keys = [...by.keys()].sort();
  const lastKey = keys.at(-1);
  if (!lastKey) return { weeks: [], weekdays: Array<number | null>(7).fill(null), max: 0, min: 0 };
  const lastMs = Date.parse(`${lastKey}T00:00:00Z`);
  const mondayOf = (ms: number) => ms - ((new Date(ms).getUTCDay() + 6) % 7) * DAY;
  const lastMonday = mondayOf(lastMs);
  const firstMonday = Math.max(mondayOf(Date.parse(`${keys[0]}T00:00:00Z`)), lastMonday - (weeks - 1) * 7 * DAY);
  const out: Heatmap['weeks'] = [];
  const perWeekday: number[][] = Array.from({ length: 7 }, () => []);
  let max = -Infinity;
  let min = Infinity;
  for (let m = firstMonday; m <= lastMonday; m += 7 * DAY) {
    const cells = Array.from({ length: 7 }, (_, i) => {
      const day = tornDay(m + i * DAY);
      const v = by.get(day) ?? null;
      if (v !== null) {
        perWeekday[i]!.push(v);
        max = Math.max(max, v);
        min = Math.min(min, v);
      }
      return { day, v };
    });
    out.push({ monday: tornDay(m), cells });
  }
  return { weeks: out, weekdays: perWeekday.map((xs) => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null)), max: Number.isFinite(max) ? max : 0, min: Number.isFinite(min) ? min : 0 };
}

// ---------------------------------------------------------------- the weekly star forecast

export interface StarForecast {
  rating: number | null;
  weeklyIncome: number | null;
  /** Companies of the type by rating: how many, and their middle weekly income. */
  byRating: Array<{ rating: number; companies: number; median: number }>;
  /** 'up': at or above the middle of the star above; 'down': below the middle of the star below; else 'hold'. */
  verdict: 'up' | 'hold' | 'down' | 'unknown';
  line: string;
  /** The Torn days your rating changed on (from the kept days). */
  changes: Array<{ day: string; from: number; to: number }>;
}

const median = (xs: number[]): number => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : Math.round((s[m - 1]! + s[m]!) / 2);
};

/**
 * Will the stars go up or down at Torn's weekly re-rating? Torn doesn't publish its formula, so this is a rule of thumb:
 * your week's income against the middle weekly income of the companies of your type one star above (and one below) —
 * from the public directory. Plus the days your rating changed on, from the kept days.
 */
export function starForecast(mine: { rating: number | null; weeklyIncome: number | null; id?: number }, directory: readonly Competitor[], days: readonly CompanyDay[] = []): StarForecast {
  const groups = new Map<number, number[]>();
  for (const c of directory) {
    if (c.id === mine.id || c.rating === null || c.weeklyIncome === null) continue;
    const g = groups.get(c.rating) ?? [];
    g.push(c.weeklyIncome);
    groups.set(c.rating, g);
  }
  const byRating = [...groups.entries()].map(([rating, xs]) => ({ rating, companies: xs.length, median: median(xs) })).sort((a, b) => a.rating - b.rating);
  const sorted = [...days].sort((a, b) => a.day.localeCompare(b.day));
  const changes: StarForecast['changes'] = [];
  for (let i = 1; i < sorted.length; i++) {
    const a = sorted[i - 1]!.rating;
    const b = sorted[i]!.rating;
    if (a !== null && b !== null && a !== b) changes.push({ day: sorted[i]!.day, from: a, to: b });
  }
  const { rating, weeklyIncome } = mine;
  if (rating === null || weeklyIncome === null) return { rating, weeklyIncome, byRating, verdict: 'unknown', line: 'No rating or weekly income to compare.', changes };
  const above = byRating.find((g) => g.rating === rating + 1);
  const below = byRating.find((g) => g.rating === rating - 1);
  const here = byRating.find((g) => g.rating === rating);
  if (rating < 10 && above && weeklyIncome >= above.median) return { rating, weeklyIncome, byRating, verdict: 'up', line: `Your week’s income (${usd(weeklyIncome)}) is at or above the middle of the ${rating + 1}★ companies of your type (${usd(above.median)}): a star up is likely.`, changes };
  if (rating > 1 && below && weeklyIncome < below.median) return { rating, weeklyIncome, byRating, verdict: 'down', line: `Your week’s income (${usd(weeklyIncome)}) is below the middle of the ${rating - 1}★ companies of your type (${usd(below.median)}): a star down is possible.`, changes };
  if (!above && !below && !here) return { rating, weeklyIncome, byRating, verdict: 'unknown', line: 'No companies of your type near your rating to compare with.', changes };
  return { rating, weeklyIncome, byRating, verdict: 'hold', line: `Your week’s income (${usd(weeklyIncome)}) sits with the ${rating}★ companies of your type${here ? ` (middle ${usd(here.median)})` : ''}: the same stars are likely.`, changes };
}

// ---------------------------------------------------------------- pricing advice

export interface PriceAdvice {
  name: string;
  price: number;
  cost: number;
  rrp: number;
  /** Price over cost, a unit. */
  margin: number;
  /** Price against the RRP, % (100 = at RRP). */
  ofRrp: number | null;
  /** Days the stock (with what's on order) lasts at the last day's sales (null: nothing sold). */
  daysLeft: number | null;
  /** From the kept days: each price it was sold at, its days, the average sold a day and the profit a day it made. */
  seen: Array<{ price: number; days: number; soldPerDay: number; profitPerDay: number }>;
  /** Plain-words advice (rules of thumb). */
  tips: string[];
}

/**
 * Rules of thumb per product, from its stock now and the kept days: out of stock or running out (order more), selling at a
 * loss, priced over the RRP (Torn's customers buy less), and — from the days kept — the price that made the most profit
 * a day. Not Torn's formula; labelled as advice on the page.
 */
export function pricingAdvice(stock: readonly StockDay[], days: readonly CompanySnapshot[] = []): PriceAdvice[] {
  return stock.map((p) => {
    const margin = p.price - p.cost;
    const ofRrp = p.rrp > 0 ? Math.round((p.price / p.rrp) * 1000) / 10 : null;
    const daysLeft = p.soldAmount > 0 ? Math.round(((p.inStock + p.onOrder) / p.soldAmount) * 10) / 10 : null;
    const at = new Map<number, { days: number; sold: number; profit: number }>();
    for (const d of days) {
      const s = d.stock.find((x) => x.name === p.name);
      if (!s || s.price <= 0) continue;
      const g = at.get(s.price) ?? { days: 0, sold: 0, profit: 0 };
      g.days++;
      g.sold += s.soldAmount;
      g.profit += (s.price - s.cost) * s.soldAmount;
      at.set(s.price, g);
    }
    const seen = [...at.entries()].map(([price, g]) => ({ price, days: g.days, soldPerDay: Math.round(g.sold / g.days), profitPerDay: Math.round(g.profit / g.days) })).sort((a, b) => a.price - b.price);
    const tips: string[] = [];
    if (p.inStock + p.onOrder <= 0) tips.push('Out of stock: every customer who wanted it went without — order more.');
    else if (daysLeft !== null && daysLeft < 3) tips.push(`About ${daysLeft} day${daysLeft === 1 ? '' : 's'} of stock left at yesterday’s sales — order more.`);
    if (margin < 0) tips.push(`Sold below cost (${usd(-margin)} lost on each).`);
    if (ofRrp !== null && ofRrp > 100) tips.push(`Priced ${Math.round((ofRrp - 100) * 10) / 10}% over the RRP: customers tend to buy less above it.`);
    const best = seen.filter((s) => s.days >= 2).sort((a, b) => b.profitPerDay - a.profitPerDay)[0];
    if (best && seen.filter((s) => s.days >= 2).length > 1) tips.push(best.price === p.price ? `Your current price made the most profit a day of the prices kept (${usd(best.profitPerDay)}).` : `At ${usd(best.price)} it made the most profit a day of the prices kept (${usd(best.profitPerDay)} over ${best.days} days).`);
    if (!tips.length) tips.push('Nothing stands out.');
    return { name: p.name, price: p.price, cost: p.cost, rrp: p.rrp, margin, ofRrp, daysLeft, seen, tips };
  });
}

// ---------------------------------------------------------------- employees over time (director)

export interface StaffTrend {
  id: number;
  name: string;
  /** Effectiveness on the newest day and `span` days before (or the oldest kept within it). */
  now: number | null;
  then: number | null;
  change: number | null;
  /** Days on the staff list kept. */
  days: number;
}

/** Each employee's effectiveness now against `span` days before (from the director's kept staff lists). */
export function staffTrends(days: readonly CompanySnapshot[], span = 7): StaffTrend[] {
  const list = [...days].filter((d) => d.staff.length).sort((a, b) => a.day.localeCompare(b.day));
  const last = list.at(-1);
  if (!last) return [];
  const cut = tornDay(Date.parse(`${last.day}T00:00:00Z`) - span * DAY);
  return last.staff.map((e) => {
    const seen = list.filter((d) => d.staff.some((x) => x.id === e.id));
    // The newest day at least `span` days back, else the oldest kept before the last.
    const thenDay = seen.filter((d) => d.day <= cut).at(-1) ?? seen.find((d) => d !== last);
    const then = thenDay?.staff.find((x) => x.id === e.id)?.effectiveness ?? null;
    const now = e.effectiveness;
    return { id: e.id, name: e.name, now, then, change: now !== null && then !== null ? now - then : null, days: seen.length };
  });
}
